/**
 * DownloadStatBadge — 모듈 상세 다운로드 수 + '인기' 배지 (queue #662 §UAT-2026-07-09-R662
 * §R662-3 · queue #678 §UAT-2026-07-13-R678 §R678-4). `useDownloadStats()`(공유 쿼리키
 * `['download-stats']`)의 `byModule` 에서 이 노드의 다운로드 카운트를, `topModules` 멤버십으로
 * '인기' 여부를 판정한다(임계 하드코딩 없음 — #678 이후 멤버십은 복합점수 기준이라 배지
 * 기준이 자동으로 복합점수화된다). 배지/카운트 옆에 타입별 내역(다운로드/담기/복사) 툴팁을
 * 추가(§R678-4). 부가 지표라 로딩/실패 시 조용히 아무것도 렌더하지 않는다(fail-soft — 카탈로그
 * 본체 무중단). `ModuleDetailBody` 에서 `MaturityBadge` 직후에 삽입된다.
 */
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import { useDownloadStats } from '../hooks/useDownloadStats';
import type { ModuleNode } from '../types/showcase';

interface Props {
  node: ModuleNode;
}

export function DownloadStatBadge({ node }: Props) {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useDownloadStats();

  if (isLoading || isError || !data) return null;

  const stat = data.byModule.find((m) => m.code === node.code);
  const count = stat?.count ?? 0;
  const isPopular = data.topModules.some((m) => m.code === node.code);
  // queue #678 §R678-4: 내역 툴팁 — 구 BE(#678 미배포) 응답엔 downloads/takes/copies 가
  // 없을 수 있어 downloads 는 count 로 폴백, takes/copies 는 0 폴백(NON_NULL undefined 관례).
  const breakdown = t('showcase.download.breakdown', {
    downloads: stat?.downloads ?? count,
    takes: stat?.takes ?? 0,
    copies: stat?.copies ?? 0,
  });

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--spacing-2)',
        marginBottom: 'var(--spacing-3)',
      }}
    >
      <span data-download-count title={breakdown} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
        {t('showcase.download.count', { count })}
      </span>
      {isPopular && (
        <span
          data-popular-badge
          title={breakdown}
          aria-label={t('showcase.download.popularBadgeAria', { code: node.code })}
        >
          <StatusPill tone="info">{t('showcase.download.popularBadge')}</StatusPill>
        </span>
      )}
    </div>
  );
}
